import { randomBetween, seededRandom, type ValidationResult } from "./types.ts";

export type LearningAreaId =
  | "quantity_quay"
  | "coordinate_crossroads"
  | "vector_valley"
  | "algebra_academy"
  | "geometry_gulch"
  | "trigonometry_tower"
  | "matrix_manor"
  | "newtonian_nexus"
  | "collision_canyon"
  | "sat_summit"
  | "quaternion_quarters";

export type SpecGuildId = "pathfinder" | "builder" | "guardian" | "sage";
export type SupportTier = "independent" | "standard" | "guided" | "mentor";

export type AnswerField =
  | {
      key: string;
      label: string;
      type: "number";
      unit?: string;
      min?: number;
      max?: number;
      step?: number;
    }
  | {
      key: string;
      label: string;
      type: "text";
      placeholder?: string;
    }
  | {
      key: string;
      label: string;
      type: "boolean";
    }
  | {
      key: string;
      label: string;
      type: "choice";
      choices: Array<{ id: string; label: string }>;
    };

export interface PuzzleScoringRules {
  accuracyWeight: number;
  efficiencyWeight: number;
  persistenceWeight: number;
  difficultyWeight: number;
  passingScore: number;
}

export interface PuzzleGenerationContext {
  seed: number;
  difficulty?: number;
  guild?: SpecGuildId | string;
  supportTier?: SupportTier;
}

export interface PuzzleAttemptMetrics {
  elapsedSeconds?: number;
  hintCount?: number;
  attemptNumber?: number;
}

export interface PuzzleVisualConfig {
  kind:
    | "grid"
    | "number-system"
    | "vector"
    | "coordinate"
    | "equation"
    | "trajectory"
    | "transform"
    | "physics"
    | "collision"
    | "sat";
  width?: number;
  height?: number;
  elements?: Array<Record<string, unknown>>;
}

export interface PuzzleInstance<TState extends Record<string, unknown> = Record<string, unknown>> {
  prompt: string;
  state: TState;
  visual: PuzzleVisualConfig;
  hints: string[];
  expectedAnswer: {
    fields: AnswerField[];
    instructions?: string;
  };
  scoring: PuzzleScoringRules;
}

export interface PuzzleTemplate<
  TConfig extends Record<string, unknown> = Record<string, unknown>,
  TState extends Record<string, unknown> = Record<string, unknown>,
> {
  id: string;
  title: string;
  area: LearningAreaId;
  skills: string[];
  interaction: string;
  competencyIds: string[];
  defaultConfig: TConfig;
  generateInstance(
    config: Partial<TConfig> | undefined,
    context: PuzzleGenerationContext,
  ): PuzzleInstance<TState>;
  validateAnswer(
    config: Partial<TConfig> | undefined,
    instance: PuzzleInstance<TState>,
    answer: Record<string, unknown>,
  ): ValidationResult;
  scoreAttempt(
    validation: ValidationResult,
    metrics?: PuzzleAttemptMetrics,
    context?: PuzzleGenerationContext,
  ): ValidationResult;
}

export const defaultScoringRules: PuzzleScoringRules = {
  accuracyWeight: 0.6,
  efficiencyWeight: 0.15,
  persistenceWeight: 0.15,
  difficultyWeight: 0.1,
  passingScore: 70,
};

export function createPuzzleTemplate<
  TConfig extends Record<string, unknown>,
  TState extends Record<string, unknown>,
>(
  template: Omit<PuzzleTemplate<TConfig, TState>, "scoreAttempt"> & {
    scoreAttempt?: PuzzleTemplate<TConfig, TState>["scoreAttempt"];
  },
): PuzzleTemplate<TConfig, TState> {
  return {
    ...template,
    scoreAttempt:
      template.scoreAttempt ??
      ((validation, metrics, context) =>
        scorePuzzleAttempt(validation, metrics, context)),
  };
}

export function scorePuzzleAttempt(
  validation: ValidationResult,
  metrics: PuzzleAttemptMetrics = {},
  context: PuzzleGenerationContext = { seed: 0 },
): ValidationResult {
  const accuracy = clamp(validation.score, 0, 100);
  const elapsedPenalty = Math.min(35, (metrics.elapsedSeconds ?? 0) / 6);
  const hintPenalty = (metrics.hintCount ?? 0) * 8;
  const efficiency = clamp(100 - elapsedPenalty - hintPenalty, 0, 100);
  const attemptNumber = Math.max(1, metrics.attemptNumber ?? 1);
  const persistence = attemptNumber === 1 ? 85 : clamp(70 + attemptNumber * 5, 0, 100);
  const difficulty = clamp(((context.difficulty ?? 1) - 1) * 20, 0, 100);
  let score = Math.round(
    accuracy * defaultScoringRules.accuracyWeight +
      efficiency * defaultScoringRules.efficiencyWeight +
      persistence * defaultScoringRules.persistenceWeight +
      difficulty * defaultScoringRules.difficultyWeight,
  );

  score = applySupportTierModifier(score, context.supportTier);
  score = applyGuildModifier(score, context.guild, validation.success);

  return {
    ...validation,
    score,
    success: validation.success && score >= defaultScoringRules.passingScore,
    details: {
      ...(validation.details ?? {}),
      accuracy,
      efficiency,
      persistence,
      difficulty,
    },
  };
}

export function applySupportTierModifier(score: number, tier: SupportTier = "standard") {
  if (tier === "independent") return clamp(score + 5, 0, 100);
  if (tier === "guided") return clamp(score - 3, 0, 100);
  if (tier === "mentor") return clamp(score - 6, 0, 100);
  return clamp(score, 0, 100);
}

export function applyGuildModifier(
  score: number,
  guild: PuzzleGenerationContext["guild"],
  success: boolean,
) {
  if (guild === "guardian" && !success) return clamp(score + 5, 0, 100);
  if (guild === "pathfinder" && success) return clamp(score + 2, 0, 100);
  return clamp(score, 0, 100);
}

export function getRandom(context: PuzzleGenerationContext) {
  return seededRandom(context.seed);
}

export function mergeConfig<TConfig extends Record<string, unknown>>(
  defaults: TConfig,
  config?: Partial<TConfig>,
) {
  return { ...defaults, ...(config ?? {}) } as TConfig;
}

export function integerBetween(random: () => number, min: number, max: number) {
  return Math.round(randomBetween(random, min, max));
}

export function nearlyEqual(actual: number, expected: number, tolerance: number) {
  return Math.abs(actual - expected) <= tolerance;
}

export function numberAnswer(answer: Record<string, unknown>, key: string) {
  const value = answer[key];
  return typeof value === "number" ? value : Number(value);
}

export function textAnswer(answer: Record<string, unknown>, key: string) {
  const value = answer[key];
  return typeof value === "string" ? value.trim() : String(value ?? "").trim();
}

export function booleanAnswer(answer: Record<string, unknown>, key: string) {
  const value = answer[key];
  return value === true || value === "true";
}

export function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

export function normalizeDegrees(value: number) {
  return ((value % 360) + 360) % 360;
}
